import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import "./TableAssignment.css";

interface Protocole {
  _id: string;
  name: string;
  phone: string;
}

interface TableInfo {
  name: string;
  inviteCount: number;
  assignedProtocoleId?: string;
  assignedProtocoleName?: string;
}

interface MariageInfo {
  bride: string;
  groom: string;
  date: string;
  totalInvites: number;
}

interface MariageResponse {
  success: boolean;
  mariage: {
    bride: string;
    groom: string;
    date: string;
  };
}

interface UsersResponse {
  success: boolean;
  users: Protocole[];
}

interface TablesResponse {
  tables: string[];
  counts: Record<string, number>;
  totalInvites: number;
}

interface AssignmentsResponse {
  assignments: Record<string, string>;
}

interface ApiResponse {
  success: boolean;
  message?: string;
}

const TableAssignment: React.FC<{ mariageId: string }> = ({ mariageId }) => {
  const [protocoles, setProtocoles] = useState<Protocole[]>([]);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [mariage, setMariage] = useState<MariageInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalTables: 0,
    assignedTables: 0,
    unassignedTables: 0,
    protocolesWithoutTables: 0
  });

  const fetchData = useCallback(async () => {
    try {
      const [mariageRes, protocolesRes, tablesRes, assignmentsRes] = await Promise.all([
        api.get<MariageResponse>(`/mariages/${mariageId}`),
        api.get<UsersResponse>(`/admin/users?mariageId=${mariageId}&role=protocole`),
        api.get<TablesResponse>(`/mariages/${mariageId}/tables`),
        api.get<AssignmentsResponse>(`/admin/tables/${mariageId}`)
      ]);
      
      if (mariageRes.data.success) {
        setMariage({
          bride: mariageRes.data.mariage.bride,
          groom: mariageRes.data.mariage.groom,
          date: mariageRes.data.mariage.date,
          totalInvites: tablesRes.data.totalInvites || 0
        });
      }
      
      const protocolesList: Protocole[] = protocolesRes.data.users || [];
      setProtocoles(protocolesList);
      
      const protocolesMap: Record<string, string> = {};
      protocolesList.forEach((p: Protocole) => {
        protocolesMap[p._id] = p.name;
      });
      
      const tablesWithAssignments: TableInfo[] = tablesRes.data.tables.map((table: string) => ({
        name: table,
        inviteCount: tablesRes.data.counts[table] || 0,
        assignedProtocoleId: assignmentsRes.data.assignments[table],
        assignedProtocoleName: assignmentsRes.data.assignments[table] ? protocolesMap[assignmentsRes.data.assignments[table]] : undefined
      }));
      
      setTables(tablesWithAssignments);
      
      const assignedCount = tablesWithAssignments.filter((t: TableInfo) => t.assignedProtocoleId).length;
      const protocolesWithTables = new Set(
        tablesWithAssignments
          .filter((t: TableInfo) => t.assignedProtocoleId)
          .map((t: TableInfo) => t.assignedProtocoleId)
      ).size;
      
      setStats({
        totalTables: tablesWithAssignments.length,
        assignedTables: assignedCount,
        unassignedTables: tablesWithAssignments.length - assignedCount,
        protocolesWithoutTables: protocolesList.length - protocolesWithTables
      });
      
    } catch (error) {
      console.error("Erreur chargement données:", error);
    } finally {
      setLoading(false);
    }
  }, [mariageId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssign = async (tableName: string, protocoleId: string) => {
    try {
      await api.post<ApiResponse>('/admin/tables/assign', {
        mariageId,
        tableName,
        protocoleId: protocoleId || null
      });
      
      fetchData();
    } catch (error) {
      console.error("Erreur assignation:", error);
    }
  };

  const getProtocoleTables = (protocoleId: string): string => {
    return tables
      .filter((t: TableInfo) => t.assignedProtocoleId === protocoleId)
      .map((t: TableInfo) => t.name)
      .join(", ");
  };

  if (loading) {
    return <div className="loading">Chargement des données...</div>;
  }

  return (
    <div className="table-assignment">
      {mariage && (
        <div className="mariage-header">
          <h2>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
            </svg>
            {mariage.bride} & {mariage.groom}
            <span className="mariage-date"> - {mariage.date}</span>
          </h2>
          <div className="global-stats">
            <div className="stat">
              <span className="stat-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                </svg>
                Total invités
              </span>
              <span className="stat-value">{mariage.totalInvites}</span>
            </div>
            <div className="stat">
              <span className="stat-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Tables
              </span>
              <span className="stat-value">{stats.totalTables}</span>
            </div>
            <div className="stat">
              <span className="stat-label">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                </svg>
                Protocoles
              </span>
              <span className="stat-value">{protocoles.length}</span>
            </div>
          </div>
        </div>
      )}

      <div className="tables-section">
        <h3>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
          </svg>
          LISTE DES TABLES
        </h3>
        
        <div className="tables-grid">
          {tables.map((table: TableInfo) => (
            <div key={table.name} className={`table-card ${table.assignedProtocoleId ? 'assigned' : 'unassigned'}`}>
              <div className="table-name">
                <span className="table-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                  </svg>
                </span>
                <strong>TABLE "{table.name}"</strong>
                {!table.assignedProtocoleId && <span className="unassigned-badge">Non assignée</span>}
              </div>
              
              <div className="table-details">
                <div className="table-invites">
                  <span className="invite-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                    </svg>
                  </span>
                  <span>{table.inviteCount} invité{table.inviteCount > 1 ? 's' : ''}</span>
                </div>
                
                <div className="table-assignment-info">
                  <span className="assign-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                    </svg>
                  </span>
                  {table.assignedProtocoleName ? (
                    <span>Assignée à: <strong>{table.assignedProtocoleName}</strong></span>
                  ) : (
                    <span className="unassigned-text">Non assignée</span>
                  )}
                </div>
              </div>
              
              <div className="table-actions">
                <select
                  value={table.assignedProtocoleId || ""}
                  onChange={(e) => handleAssign(table.name, e.target.value)}
                  className="protocole-select"
                >
                  <option value="">— Non assigné —</option>
                  {protocoles.map((p: Protocole) => (
                    <option key={p._id} value={p._id}>
                      {p.name} {table.assignedProtocoleId === p._id ? '(actuel)' : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="protocoles-section">
        <h3>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
          </svg>
          RÉPARTITION PAR PROTOCOLE
        </h3>
        
        <div className="protocoles-grid">
          {protocoles.map((protocole: Protocole) => {
            const tablesList = getProtocoleTables(protocole._id);
            return (
              <div key={protocole._id} className={`protocole-card ${!tablesList ? 'empty' : ''}`}>
                <div className="protocole-name">
                  <strong>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" fill="currentColor"/>
                    </svg>
                    {protocole.name}
                  </strong>
                  <span className="protocole-phone">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" fill="currentColor"/>
                    </svg>
                    {protocole.phone}
                  </span>
                </div>
                <div className="protocole-tables">
                  {tablesList ? (
                    <span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" fill="currentColor"/>
                      </svg>
                      {tablesList}
                    </span>
                  ) : (
                    <span className="no-tables">Aucune table assignée</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="stats-section">
        <h3>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor"/>
          </svg>
          STATISTIQUES
        </h3>
        <div className="stats-grid">
          <div className="stat-card">
            <span className="stat-badge assigned">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
              </svg>
              {stats.assignedTables}
            </span>
            <span>Tables assignées</span>
          </div>
          <div className="stat-card">
            <span className="stat-badge unassigned">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
              </svg>
              {stats.unassignedTables}
            </span>
            <span>Tables non assignées</span>
          </div>
          <div className="stat-card">
            <span className="stat-badge empty">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
              </svg>
              {stats.protocolesWithoutTables}
            </span>
            <span>Protocoles sans table</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TableAssignment;